import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle2, Percent } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import DirectAnswer from '../components/DirectAnswer';
import { Button } from '../components/ui/button';
import { pricingPageSEO, PUBLIC_SITE_URL } from '../data/seo';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { pricingCategories, financeSummary, pricingFAQs, PRICING_LAST_UPDATED } from '../data/pricing';
import { getOfferCatalogLD, getBreadcrumbLD, getFAQPageLD, getSpeakableLD } from '../data/structuredData';

const PriceTable = ({ category }) => (
  <section id={category.id} className="scroll-mt-28" aria-labelledby={`h-${category.id}`} data-testid={`price-table-${category.id}`}>
    <h2 id={`h-${category.id}`} className="text-2xl md:text-3xl font-bold text-rabbit mb-4">{category.title}</h2>
    <div className="overflow-x-auto rounded-xl border border-sky-gray/20">
      <table className="w-full text-left text-sm md:text-base">
        <thead className="bg-sky-gray/10 text-rabbit">
          <tr>
            <th scope="col" className="px-4 py-3 font-semibold">Treatment</th>
            <th scope="col" className="px-4 py-3 font-semibold whitespace-nowrap">Price (GBP)</th>
          </tr>
        </thead>
        <tbody>
          {category.items.map((item) => (
            <tr key={item.treatment} className="border-t border-sky-gray/15 hover:bg-amberlight/10">
              <td className="px-4 py-3 text-chocolate-malt">
                <Link to={item.link} className="font-medium text-rabbit hover:text-burnt-coral">{item.treatment}</Link>
                {item.note && <span className="block text-xs text-chocolate-malt/80 mt-1">{item.note}</span>}
              </td>
              <td className="px-4 py-3 font-semibold text-[#006A71] whitespace-nowrap">{item.price}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default function Pricing() {
  const crumbs = [{ name: 'Home', to: '/' }, { name: 'Fees & Prices', to: '/pricing' }];
  return (
    <div className="min-h-screen bg-white">
      <SEO
        {...pricingPageSEO}
        structuredData={[
          getOfferCatalogLD(pricingCategories),
          getBreadcrumbLD(crumbs.map((c) => ({ name: c.name, url: `${PUBLIC_SITE_URL}${c.to}` }))),
          getFAQPageLD(pricingFAQs),
          getSpeakableLD(['Invisalign from £2,800, dental implants from £2,295, composite bonding from £250 per tooth']),
        ]}
      />
      <PageHeader />
      <main className="pt-28 md:pt-32">
        <section className="container mx-auto px-4 max-w-5xl">
          <Breadcrumbs items={crumbs} />
          <p className="text-sm font-semibold uppercase tracking-widest text-[#006A71] mt-6 mb-3">Transparent fee guide · Acton, West London</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-rabbit leading-tight mb-6">Dental Treatment Prices in Acton</h1>
          <DirectAnswer
            question="How much does dental treatment cost at Restore.Dental?"
            answer={`Invisalign starts from £2,800, a single dental implant with crown from £2,295, composite bonding from £250 per tooth and professional teeth whitening from £350. New-patient consultations are free when you proceed with treatment, and every plan can be spread over 3–60 months at 0% interest. Fee guide last updated ${new Date(PRICING_LAST_UPDATED).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}.`}
            bullets={['Written, itemised quote before any treatment', 'No hidden fees for X-rays or reviews', '0% finance on £500 – £30,000', 'Free consultation when you proceed']}
          />

          <nav aria-label="Jump to price category" className="flex flex-wrap gap-2 mt-8">
            {pricingCategories.map((c) => (
              <a key={c.id} href={`#${c.id}`} className="px-4 py-2 rounded-full border border-sky-gray/30 text-sm text-rabbit hover:bg-sky-gray/10" data-testid={`jump-${c.id}`}>{c.title}</a>
            ))}
          </nav>
        </section>

        <section className="container mx-auto px-4 max-w-5xl mt-12 space-y-14">
          {pricingCategories.map((c) => <PriceTable key={c.id} category={c} />)}
          <p className="text-xs text-chocolate-malt/70">Prices are a guide and depend on clinical complexity. You will always receive a written quote after examination before any treatment begins. Prices include VAT where applicable.</p>
        </section>

        <section className="bg-sky-gray/10 py-16 mt-16">
          <div className="container mx-auto px-4 max-w-5xl grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#01A9B4]/30 text-[#006A71] text-sm font-semibold mb-4"><Percent className="w-4 h-4" aria-hidden="true" />0% APR representative</div>
              <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">{financeSummary.headline}</h2>
              <ul className="space-y-3 text-chocolate-malt">
                {financeSummary.bullets.map((b) => <li key={b} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span>{b}</span></li>)}
              </ul>
              <Button asChild size="lg" className="mt-6 bg-burnt-coral hover:bg-burnt-coral/90 text-white"><Link to="/finance" data-testid="pricing-finance-link">Calculate your monthly payment</Link></Button>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-sky-gray/20">
              <h3 className="text-xl font-bold text-rabbit mb-3">Get an exact quote</h3>
              <p className="text-chocolate-malt mb-6">Send a photo of your smile on WhatsApp and Dr Patel&apos;s team will give you an indicative price range within one working day.</p>
              <div className="flex flex-col gap-3">
                <Button asChild className="bg-[#25D366] hover:bg-[#20BA5A] text-white"><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="pricing-whatsapp-btn">WhatsApp {PRACTICE.phoneDisplay}</a></Button>
                <Button asChild variant="outline"><a href={PRACTICE.phoneTel} data-testid="pricing-call-btn"><Phone className="w-4 h-4 mr-2" />Call the practice</a></Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">Dental pricing FAQs</h2>
            <div className="space-y-6">
              {pricingFAQs.map((f) => (
                <div key={f.question} className="border-b border-sky-gray/20 pb-6">
                  <h3 className="text-lg font-bold text-rabbit mb-2">{f.question}</h3>
                  <p className="text-chocolate-malt leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
